import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Snackbar,
    Alert,
    AlertTitle,
    IconButton,
    Box,
    Typography
} from '@mui/material';
import {
    Close as CloseIcon,
    ArrowForward as ArrowIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../context/NotificationContext';

/**
 * Composant pour afficher des notifications toast
 */
const NotificationToast = () => {
    const { notifications, markAsRead } = useNotifications();
    const { t } = useTranslation('notifications');
    const [open, setOpen] = useState(false);
    const [currentNotification, setCurrentNotification] = useState(null);
    const [queue, setQueue] = useState([]);
    const navigate = useNavigate();

    // Surveiller les nouvelles notifications
    useEffect(() => {
        // Filtrer les notifications importantes (warning ou error) non lues
        const importantNotifications = notifications.filter(
            notification =>
                !notification.isRead &&
                (notification.type === 'warning' || notification.type === 'error')
        );

        // Ajouter les nouvelles notifications importantes à la file d'attente
        if (importantNotifications.length > 0) {
            setQueue(prev => [...prev, ...importantNotifications]);
        }
    }, [notifications]);

    // Gérer la file d'attente des notifications
    useEffect(() => {
        if (queue.length > 0 && !open) {
            // Prendre la première notification de la file d'attente
            const notification = queue[0];
            setCurrentNotification(notification);
            setOpen(true);

            // Retirer la notification de la file d'attente
            setQueue(prev => prev.slice(1));
        }
    }, [queue, open]);

    /**
     * Fermer la notification toast
     */
    const handleClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }

        setOpen(false);
    };

    /**
     * Marquer la notification comme lue et la fermer
     */
    const handleDismiss = () => {
        if (currentNotification) {
            markAsRead(currentNotification.id);
        }
        setOpen(false);
    };

    /**
     * Naviguer vers le lien de la notification
     */
    const handleClick = () => {
        if (currentNotification) {
            // Marquer la notification comme lue
            markAsRead(currentNotification.id);

            // Naviguer vers le lien s'il existe
            if (currentNotification.link) {
                navigate(currentNotification.link);
            }

            setOpen(false);
        }
    };

    if (!currentNotification) {
        return null;
    }

    return (
        <Snackbar
            open={open}
            autoHideDuration={6000}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
            <Alert
                severity={currentNotification.type}
                variant="filled"
                sx={{
                    width: '100%',
                    maxWidth: 400,
                    color: '#ffffff',
                    '& .MuiAlertTitle-root': {
                        color: '#ffffff'
                    }
                }}
                action={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {currentNotification.link && (
                            <IconButton
                                size="small"
                                color="inherit"
                                onClick={handleClick}
                                aria-label="Voir les détails"
                            >
                                <ArrowIcon fontSize="small" />
                            </IconButton>
                        )}
                        <IconButton
                            size="small"
                            color="inherit"
                            onClick={handleDismiss}
                            aria-label="Fermer"
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                }
            >
                <AlertTitle>
                    {currentNotification.format === 'i18n' && currentNotification.key
                        ? t(`${currentNotification.key}_title`, currentNotification.variables || {})
                        : currentNotification.title}
                </AlertTitle>
                <Typography variant="body2">
                    {currentNotification.format === 'i18n' && currentNotification.key
                        ? t(currentNotification.key, currentNotification.variables || {})
                        : currentNotification.message}
                </Typography>
            </Alert>
        </Snackbar>
    );
};

export default NotificationToast;